import { WbDatePicker } from '@agilelab/plugin-wb-platform';
import { TextFieldProps } from '@material-ui/core/TextField';
import { WidgetProps } from '@rjsf/utils';
import { FocusEvent, useMemo, useEffect } from 'react';
import { extractCustomProperties, isHidden } from '../../utils';
import { dateToString, getDateDisplayFormat } from '../../utils/dateFormat';
import { UseDependencyManagerResult } from '../DependencyManager';
import { extractFieldPathFromRjsfId } from '../utils/extractFieldPathFromRjsfId';

export { dateToString } from '../../utils/dateFormat';

export const DateWidget = ({
  id,
  placeholder,
  required,
  readonly,
  disabled,
  label,
  value,
  onChange,
  onBlur,
  onFocus,
  options,
  schema,
  uiSchema,
  rawErrors = [],
  formContext,
  registry,
  ...textFieldProps
}: WidgetProps) => {
  const customProps = uiSchema ? extractCustomProperties(uiSchema) : {};
  const ariaLabel = label || id;

  const dependencyManager = formContext?.dependencyManager as
    | UseDependencyManagerResult
    | undefined;
  const rootSchema = formContext?.rootSchema;

  const fieldPath = useMemo(() => {
    return extractFieldPathFromRjsfId(id, rootSchema);
  }, [id, rootSchema]);

  const displayFormat = getDateDisplayFormat(options);
  const stringValue = dateToString(value);

  useEffect(() => {
    if (stringValue && value !== stringValue) {
      if (dependencyManager?.notifyUpdate && fieldPath) {
        dependencyManager.notifyUpdate({
          fieldPath,
          value: stringValue,
          source: 'user',
          intent: 'manual',
        });
      } else {
        onChange(stringValue);
      }
    }
  }, [value, stringValue, onChange, dependencyManager, fieldPath]);

  const _onChange = (selectedValue?: string) => {
    const newValue = selectedValue ?? options.emptyValue;

    if (dependencyManager?.notifyUpdate && fieldPath) {
      dependencyManager.notifyUpdate({
        fieldPath,
        value: newValue,
        source: 'user',
        intent: 'manual',
      });
    } else {
      onChange(newValue);
    }
  };

  const _onBlur = (event: FocusEvent<HTMLInputElement>) =>
    onBlur(id, event.target.value);
  const _onFocus = (event: FocusEvent<HTMLInputElement>) =>
    onFocus(id, event.target.value);

  return (
    <WbDatePicker
      id={id}
      name={id}
      placeholder={placeholder}
      label={schema.title}
      ariaLabel={ariaLabel}
      style={{ display: isHidden(uiSchema) ? 'none' : undefined }}
      required={required}
      disabled={disabled || readonly}
      value={stringValue || value || ''}
      dateFormat="yyyy-MM-dd"
      displayFormat={displayFormat}
      allowKeyboardInput
      error={rawErrors.length > 0}
      onChange={_onChange}
      onBlur={_onBlur}
      onFocus={_onFocus}
      {...(textFieldProps as TextFieldProps)}
      {...customProps}
    />
  );
};
